import uiConfig from 'src/lib/ui/config';
import { ImageCoverView } from '@play-co/timestep-core/ui';
import ResponsiveScene from './ResponsiveScene';
import { Transition } from 'src/game/logic/SceneManager';
import { waitForItPromise } from 'src/lib/utils';
import { isSceneEntered } from 'src/lib/stateUtils';
import statePromise from 'src/lib/statePromise';
import StateObserver from 'src/StateObserver';
import i18n from 'src/lib/i18n/i18n';
import platform from '@play-co/gcinstant';
import { startConversation } from '../conversation/conversation.redux';
import { Pet } from 'src/replicant/buruburu/pet';
import { endVisitSession } from '../visit/visit.sequence';
import { getFriendsPet, getPet } from '../pet';
import { Duo } from 'src/replicant/duo';
import { Feature } from 'src/replicant/analytics';
import { SideButton } from '../components/SideButton';
import features from 'src/replicant/ruleset/features';
import { startDuoInviteSequence } from '../duo/duo.sequence';
import { getDynamicReceiverBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { LockedReason } from '../popups/PopupFeatureDisabled';

export default class FriendsLivingRoomScene extends ResponsiveScene {
  private duoButton: SideButton;

  constructor() {
    super(
      new ImageCoverView({
        ...uiConfig.buruburu.backgroundDefaults,
        ...uiConfig.buruburu.livingRoom,
      }),
    );

    this.getView().tag = 'friendsLivingRoom';

    this.createGoHomeButton();
    this.createDuoButton();
  }

  public onBeforeTransition() {
    const { user } = StateObserver.getState();

    this.duoButton.updateOpts({
      visible:
        // Feature should be enabled.
        features(user).duo &&
        // Friend should also be in the enabled bucket.
        false,
    });

    const {
      isUnlocked,
      hasPendingSentInvitations,
      hasPendingReceivedInvitations,
    } = Duo.api;
    const locked = !isUnlocked(user);
    const pendingSentInvitations = hasPendingSentInvitations(
      user,
      StateObserver.now(),
    );
    const pendingReceivedInvitations = hasPendingReceivedInvitations(
      user,
      StateObserver.now(),
    );
    const areBothPetsAdults =
      getPet().stage === 'adult' && getFriendsPet().stage === 'adult';

    const shouldBeLocked =
      locked ||
      pendingSentInvitations ||
      pendingReceivedInvitations ||
      !areBothPetsAdults;

    let reason: LockedReason = null;

    if (pendingSentInvitations) {
      reason = 'pendingInvitation';
    } else if (pendingReceivedInvitations) {
      reason = 'pendingResponse';
    } else if (areBothPetsAdults) {
      reason = 'notAdult';
    }

    this.duoButton.setLocked(shouldBeLocked, reason);
  }

  public async showScene() {
    await statePromise(() => isSceneEntered('friendsLivingRoom'));
    await waitForItPromise(500);

    getPet().position.setWandering(true);
    getFriendsPet().position.setWandering(true);
  }

  public hideScene() {
    getPet().position.setWandering(false);
    getFriendsPet().position.setWandering(false);
  }

  public transition(): Transition {
    const { ui } = StateObserver.getState();

    return ui.transition.previousScene !== 'livingRoom'
      ? 'slide_back'
      : 'slide_forward';
  }

  private createGoHomeButton() {
    new SideButton({
      superview: this.getView(),
      type: 'visitFriend',
      x: 619,
      y: 224,
      onClick: () => this.goHome(),
      label: i18n('friendsVisit.livingRoom.goHome'),
      icon: {
        image: 'assets/ui/visit/icons/home_icon.png',
      },
      tracking: {
        feature: 'friends-visit',
        $subFeature: 'friends-visit-go-home',
      },
    });
  }

  private async goHome() {
    const state = StateObserver.getState();
    const { user } = state;
    const { friendId } = user.visit.visitSession ?? {};
    if (!friendId) {
      return;
    }

    const { getCurrentEvolution } = Pet.getters(user);

    await startConversation(
      platform.playerID,
      'friend_goodby',
      getCurrentEvolution().species,
      'normal', // The pet state is not relevant for this dialog.
    );

    await startConversation(
      friendId,
      'friend_farewell',
      state.pet[friendId].species,
      'normal', // The pet state is not relevant for this dialog.
    );

    // End visit session.
    await endVisitSession(friendId);
  }

  private createDuoButton() {
    this.duoButton = new SideButton({
      superview: this.getView(),
      x: 619,
      y: 396,
      type: 'duo',
      onClick: () => startDuoInviteSequence(),
      label: i18n('friendsVisit.livingRoom.formDuo'),
      icon: {
        image: 'assets/ui/duo/icons/duo_icon.png',
        width: 140,
        height: 117,
        offsetX: 10,
        offsetY: -5,
      },
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.FORM_DUO,
      },
    });
  }
}
